import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Upload, X, Link2, Ban, CheckCircle2, RotateCw, Landmark } from "lucide-react";

export default function AdminBankRecon() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("unmatched");
  const [importing, setImporting] = useState(false);
  const [matching, setMatching] = useState(null);
  const [accounts, setAccounts] = useState([]);

  const load = async () => {
    try {
      const [tx, acc] = await Promise.all([
        api.get(`/finance/bank/transactions?status=${filter}`),
        api.get("/accounts"),
      ]);
      setRows(tx.data || []);
      setAccounts(acc.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const ignore = async (r) => {
    if (!confirm("Ignore this bank row? It won't count in reconciliation.")) return;
    try { await api.post(`/finance/bank/transactions/${r.bank_tx_id}/ignore`); toast.success("Ignored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ignore failed"); }
  };

  return (
    <div data-testid="admin-bank-recon" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Finance</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
            Bank Reconciliation
          </h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            Import a bank statement CSV, then match each row to an existing journal entry or create a fresh one.
            Positive amounts = deposits · negative = withdrawals.
          </p>
        </div>
        <button onClick={() => setImporting(true)} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="import-bank-btn">
          <Upload className="w-4 h-4" /> Import statement
        </button>
      </div>

      <div className="flex gap-1 border-b border-[#E2E8F0] dark:border-slate-700">
        {["unmatched", "matched", "ignored"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`bank-filter-${s}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider transition ${
                    filter === s
                      ? "border-b-2 border-[#C9A356] text-[#0A2540] dark:text-white font-medium"
                      : "text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
                  }`}>
            {s}
          </button>
        ))}
      </div>

      <div className="card-pb overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-[#F8FAFC] dark:bg-slate-800 text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
            <tr>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-left px-4 py-2">Description</th>
              <th className="text-right px-4 py-2">Amount</th>
              <th className="text-right px-4 py-2 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="text-center py-10 text-[#94A3B8] italic">
                No {filter} transactions.
                {filter === "unmatched" && ' Click "Import statement" to add rows.'}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.bank_tx_id} data-testid={`bank-row-${r.bank_tx_id}`}
                  className="border-t border-[#E2E8F0] dark:border-slate-700 hover:bg-[#F8FAFC] dark:hover:bg-slate-800">
                <td className="px-4 py-2 text-[#475569] dark:text-slate-400">
                  {new Date(r.date).toLocaleDateString()}
                </td>
                <td className="px-4 py-2">
                  <div className="text-[#0A2540] dark:text-white inline-flex items-center gap-1.5">
                    <Landmark className="w-3 h-3 text-[#C9A356]" /> {r.description}
                  </div>
                  {r.external_id && <div className="text-[10px] font-mono text-[#94A3B8]">{r.external_id}</div>}
                </td>
                <td className={`px-4 py-2 text-right tabular-nums font-medium ${
                  r.amount > 0 ? "text-emerald-600" : "text-red-500"
                }`}>
                  {r.amount > 0 ? "+" : ""}${Math.abs(r.amount).toLocaleString()}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {r.status === "unmatched" && (
                    <>
                      <button onClick={() => setMatching(r)} className="text-xs text-[#C9A356] hover:underline mr-3 inline-flex items-center gap-0.5" data-testid={`match-btn-${r.bank_tx_id}`}>
                        <Link2 className="w-3 h-3" /> Match
                      </button>
                      <button onClick={() => ignore(r)} className="text-xs text-[#94A3B8] hover:text-red-500 inline-flex items-center gap-0.5" data-testid={`ignore-btn-${r.bank_tx_id}`}>
                        <Ban className="w-3 h-3" /> Ignore
                      </button>
                    </>
                  )}
                  {r.status === "matched" && (
                    <span className="text-xs text-emerald-600 inline-flex items-center gap-0.5">
                      <CheckCircle2 className="w-3 h-3" /> Matched
                    </span>
                  )}
                  {r.status === "ignored" && (
                    <span className="text-xs text-[#94A3B8] italic">Ignored</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {importing && <ImportModal onClose={() => setImporting(false)} onSaved={() => { setImporting(false); load(); }} />}
      {matching && <MatchModal btx={matching} accounts={accounts} onClose={() => setMatching(null)} onSaved={() => { setMatching(null); load(); }} />}
    </div>
  );
}

function ImportModal({ onClose, onSaved }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const parse = () => {
    // Accept CSV lines: date,amount,description[,external_id] — header optional
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const start = /^date/i.test(lines[0]) ? 1 : 0;
    const rows = [];
    for (let i = start; i < lines.length; i++) {
      const parts = lines[i].split(",").map((s) => s.replace(/^"|"$/g, "").trim());
      if (parts.length < 3) continue;
      rows.push({
        date: parts[0],
        amount: Number(parts[1]),
        description: parts[2],
        external_id: parts[3] || null,
      });
    }
    return rows;
  };
  const parsed = parse();

  const save = async () => {
    if (!parsed.length) { toast.error("No valid rows"); return; }
    setSaving(true);
    try {
      const r = await api.post("/finance/bank/import", { rows: parsed, account_code: "1010" });
      toast.success(`Imported ${r.data.imported} (skipped ${r.data.skipped})`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="Import bank statement" onClose={onClose}>
      <p className="text-xs text-[#475569] dark:text-slate-400 mb-2">
        Paste CSV rows: <code className="text-[10px] bg-[#F8FAFC] px-1 rounded">date,amount,description,external_id</code>. Header row optional. Bank account defaults to 1010 (Bank — operating).
      </p>
      <textarea
        rows={10}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="2026-02-05,5000,WIRE ACME LLC,BNK-1001&#10;2026-02-06,-350,OFFICE DEPOT,BNK-1002"
        data-testid="bank-import-text"
        className="w-full font-mono text-xs border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md p-2"
      />
      <div className="text-xs text-[#475569] dark:text-slate-400 mt-2" data-testid="bank-import-parsed">
        Parsed: <b>{parsed.length}</b> rows
      </div>
      <button onClick={save} disabled={saving || parsed.length === 0} className="btn-accent text-sm w-full mt-3 disabled:opacity-50" data-testid="bank-import-save">
        {saving ? "Importing…" : `Import ${parsed.length} rows`}
      </button>
    </ModalShell>
  );
}

function MatchModal({ btx, accounts, onClose, onSaved }) {
  const [offset, setOffset] = useState(btx.amount > 0 ? "4000" : "6900");
  const [memo, setMemo] = useState(btx.description);
  const [saving, setSaving] = useState(false);

  const match = async () => {
    setSaving(true);
    try {
      await api.post(`/finance/bank/transactions/${btx.bank_tx_id}/match`, {
        create: { offset_code: offset, memo },
      });
      toast.success("Matched");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Match failed"); }
    finally { setSaving(false); }
  };

  const cls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm";

  return (
    <ModalShell title="Match bank transaction" onClose={onClose}>
      <div className="bg-[#F8FAFC] dark:bg-slate-800 p-3 rounded-md text-sm mb-3">
        <div className="font-medium text-[#0A2540] dark:text-white">{btx.description}</div>
        <div className="text-xs text-[#475569] dark:text-slate-400">
          {new Date(btx.date).toLocaleDateString()} · <b className={btx.amount > 0 ? "text-emerald-600" : "text-red-500"}>
            {btx.amount > 0 ? "+" : ""}${Math.abs(btx.amount).toLocaleString()}
          </b>
        </div>
      </div>
      <p className="text-xs text-[#475569] dark:text-slate-400 mb-3">
        This will create a journal entry: <b>Bank {btx.amount > 0 ? "DR" : "CR"} ${Math.abs(btx.amount).toLocaleString()}</b> against the offset account you choose.
      </p>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Offset account *</label>
          <select className={cls} value={offset} onChange={(e) => setOffset(e.target.value)} data-testid="match-offset">
            {accounts.filter((a) => a.code !== btx.account_code).map((a) => (
              <option key={a.code} value={a.code}>{a.code} — {a.name} ({a.type})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Memo</label>
          <input className={cls} value={memo} onChange={(e) => setMemo(e.target.value)} data-testid="match-memo" />
        </div>
        <button onClick={match} disabled={saving} className="btn-accent text-sm w-full disabled:opacity-50" data-testid="match-save">
          {saving ? "Matching…" : "Match & post entry"}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
